import { ShoeboxLocation, Geolocation } from '../types/Location';

// Mapbox access token from environment variables
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";

// Google Maps API key from environment variables
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

/**
 * Generate a Mapbox static image URL for a given location
 */
export const generateMapboxStaticImageUrl = (
  geolocation: Geolocation,
  width: number = 500,
  height: number = 300,
  zoom: number = 16
): string => {
  const { latitude, longitude } = geolocation;
  return `https://api.mapbox.com/styles/v1/mapbox/light-v11/static/pin-s-l+000(${longitude},${latitude})/${longitude},${latitude},${zoom}/${width}x${height}?access_token=${MAPBOX_TOKEN}`;
};

/**
 * Check if a location has enough information to attempt geocoding
 * We don't want to geocode vague locations like "Home" or "Park"
 */
export const hasGeocodableInformation = (location: ShoeboxLocation): boolean => {
  // If we already have coordinates, no need to geocode
  if (location.geolocation) {
    return false;
  }
  
  // Check if we have at least city, state, or country
  return Boolean(
    (location.city && location.city.trim()) || 
    (location.state && location.state.trim()) || 
    (location.country && location.country.trim())
  );
};

/**
 * Geocode a location using Google Maps Geocoding API
 * Returns a promise that resolves to a Geolocation object
 */
export const geocodeLocation = async (location: ShoeboxLocation): Promise<Geolocation | null> => {
  try {
    // Build address string from available components
    const addressParts = [
      location.sublocation,
      location.city,
      location.state,
      location.country
    ].filter(Boolean);
    
    if (addressParts.length === 0) {
      return null;
    }
    
    const address = addressParts.join(', ');
    
    // Call Google Maps Geocoding API
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${GOOGLE_MAPS_API_KEY}`
    );
    
    const data = await response.json();
    
    if (data.status === 'OK' && data.results && data.results.length > 0) {
      const result = data.results[0];
      const { lat, lng } = result.geometry.location;
      
      return {
        latitude: lat,
        longitude: lng
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error geocoding location:', error);
    return null;
  }
};

// Track Google Maps loading state
let googleMapsLoadingPromise: Promise<void> | null = null;

/**
 * Load the Google Maps JavaScript API with better handling of concurrent loading attempts
 */
export const loadGoogleMapsScript = (): Promise<void> => {
  // If we already have a loading promise in progress, return that
  if (googleMapsLoadingPromise) {
    return googleMapsLoadingPromise;
  }
  
  // Check if the script is already loaded
  if (window.google && window.google.maps && window.google.maps.places) {
    return Promise.resolve();
  }
  
  // Create a new loading promise
  googleMapsLoadingPromise = new Promise<void>((resolve, reject) => {
    // Define a callback function that will be called when the API is loaded
    const callbackName = 'googleMapsApiLoaded_' + Math.random().toString(36).substring(2, 9);
    (window as any)[callbackName] = () => {
      // Check if the API is actually loaded
      if (window.google && window.google.maps && window.google.maps.places) {
        console.log('Google Maps API loaded successfully');
        resolve();
      } else {
        console.error('Google Maps API failed to load properly');
        reject(new Error('Google Maps API failed to load properly'));
      }
      // Clean up the global callback
      delete (window as any)[callbackName];
    };
    
    // Create script element with callback
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&loading=async&callback=${callbackName}`;
    script.async = true;
    script.defer = true;
    
    // Set up error handler
    script.onerror = () => {
      console.error('Failed to load Google Maps API script');
      reject(new Error('Failed to load Google Maps API script'));
      delete (window as any)[callbackName];
      googleMapsLoadingPromise = null;
    };
    
    // Add to document
    document.head.appendChild(script);
    
    // Set a timeout in case the callback is never called
    setTimeout(() => {
      if ((window as any)[callbackName]) {
        console.error('Google Maps API loading timed out');
        reject(new Error('Google Maps API loading timed out'));
        delete (window as any)[callbackName];
        googleMapsLoadingPromise = null;
      }
    }, 10000); // 10 second timeout
  }).catch(error => {
    // Reset the loading promise on error so we can try again
    googleMapsLoadingPromise = null;
    throw error;
  });
  
  return googleMapsLoadingPromise;
};

/**
 * Create a Google Maps Place Autocomplete in a container
 * This function uses the legacy Autocomplete API since PlaceAutocompleteElement
 * might not be available in all versions of the Google Maps JavaScript API
 */
export const createPlaceAutocompleteInContainer = (
  container: HTMLElement,
  onPlaceSelected: (place: google.maps.places.PlaceResult) => void
): google.maps.places.Autocomplete | null => {
  // Double-check that the API is loaded
  if (!window.google || !window.google.maps || !window.google.maps.places) {
    console.error('Google Maps API not loaded, attempting to load it now');
    // Try to load the API and then retry
    loadGoogleMapsScript()
      .then(() => {
        // Check again after loading
        if (window.google && window.google.maps && window.google.maps.places) {
          console.log('Google Maps API loaded successfully, retrying autocomplete creation');
          // Retry creating the autocomplete after a short delay
          setTimeout(() => createPlaceAutocompleteInContainer(container, onPlaceSelected), 100);
        } else {
          console.error('Google Maps API still not available after loading');
        }
      })
      .catch(error => {
        console.error('Failed to load Google Maps API:', error);
      });
    return null;
  }
  
  // Clear any existing content
  container.innerHTML = '';
  
  // Create an input element for the autocomplete
  const inputElement = document.createElement('input');
  inputElement.type = 'text';
  inputElement.placeholder = 'Search for a location';
  inputElement.style.width = '100%';
  inputElement.style.padding = '8px';
  inputElement.style.boxSizing = 'border-box';
  inputElement.style.border = '1px solid transparent'; // Make inner border transparent
  inputElement.style.borderRadius = '4px';
  inputElement.style.outline = 'none'; // Remove focus outline
  inputElement.style.fontSize = '14px';
  
  // Add focus/blur event listeners to style the input
  inputElement.addEventListener('focus', () => {
    inputElement.style.borderColor = '#228be6'; // Blue border on focus
  });
  
  inputElement.addEventListener('blur', () => {
    inputElement.style.borderColor = 'transparent';
  });
  
  // Append the input to the container
  container.appendChild(inputElement);
  
  // Initialize the autocomplete on the input
  const autocomplete = new google.maps.places.Autocomplete(inputElement, {
    types: ['geocode', 'establishment'],
    fields: ['address_components', 'geometry', 'name', 'formatted_address', 'place_id']
  });
  
  // Add listener for place selection
  autocomplete.addListener('place_changed', () => {
    const place = autocomplete.getPlace();
    if (place.geometry && place.geometry.location) {
      // Focus the input to ensure the user sees their selection
      inputElement.focus();
      onPlaceSelected(place);
    } else {
      console.warn('Place selected but no geometry found:', place);
    }
  });
  
  // Focus the input automatically
  setTimeout(() => {
    inputElement.focus();
  }, 100);
  
  return autocomplete;
};

/**
 * Try to use the modern PlaceAutocompleteElement if available,
 * otherwise fall back to the legacy Autocomplete
 * @deprecated This is a placeholder for future implementation when PlaceAutocompleteElement is widely available
 */
export const createPlaceAutocompleteElement = (
  container: HTMLElement,
  onPlaceSelected: (place: google.maps.places.PlaceResult) => void
): any => {
  // For now, always use the legacy approach
  return createPlaceAutocompleteInContainer(container, onPlaceSelected);
};

/**
 * Initialize legacy Google Maps Place Autocomplete on an input element
 * @deprecated Use createPlaceAutocompleteElement instead
 */
export const initPlaceAutocomplete = (
  inputElement: HTMLInputElement,
  onPlaceSelected: (place: google.maps.places.PlaceResult) => void
): google.maps.places.Autocomplete | null => {
  if (!window.google || !window.google.maps || !window.google.maps.places) {
    console.error('Google Maps API not loaded');
    return null;
  }
  
  console.warn('Using deprecated Autocomplete API. Consider switching to PlaceAutocompleteElement.');
  
  const autocomplete = new google.maps.places.Autocomplete(inputElement, {
    types: ['geocode']
  });
  
  autocomplete.addListener('place_changed', () => {
    const place = autocomplete.getPlace();
    if (place.geometry && place.geometry.location) {
      onPlaceSelected(place);
    }
  });
  
  return autocomplete;
};

/**
 * Extract location data from a Google Maps place result
 */
export const extractLocationFromPlace = (place: google.maps.places.PlaceResult): ShoeboxLocation => {
  const location: ShoeboxLocation = {};
  
  // Use the place name as the sublocation if available
  if (place.name) {
    location.sublocation = place.name;
  }
  
  // Add the formatted address if available
  if (place.formatted_address) {
    location.address = place.formatted_address;
  }
  
  // Extract address components
  if (place.address_components) {
    // First pass: extract standard components
    for (const component of place.address_components) {
      const types = component.types;
      
      if (types.includes('locality')) {
        location.city = component.long_name;
      } else if (types.includes('administrative_area_level_1')) {
        location.state = component.long_name;
      } else if (types.includes('country')) {
        location.country = component.long_name;
      }
    }
    
    // Extract street address (just the street part without city/state/country)
    const streetNumber = place.address_components.find(c => c.types.includes('street_number'))?.long_name;
    const route = place.address_components.find(c => c.types.includes('route'))?.long_name;
    
    if (streetNumber && route) {
      location.street_address = `${streetNumber} ${route}`;
    }
    
    // If we don't have a formatted address, try to build one from components
    if (!location.address) {
      const postalCode = place.address_components.find(c => c.types.includes('postal_code'))?.long_name;
      
      if (location.street_address) {
        location.address = location.street_address;
        if (postalCode) {
          location.address += `, ${postalCode}`;
        }
        
        // Add city, state, country if available
        if (location.city) {
          location.address += `, ${location.city}`;
        }
        if (location.state) {
          location.address += `, ${location.state}`;
        }
        if (location.country) {
          location.address += `, ${location.country}`;
        }
      }
    }
    
    // Second pass: if we don't have a sublocation yet, try to find one
    if (!location.sublocation) {
      for (const component of place.address_components) {
        const types = component.types;
        
        if (types.includes('sublocality_level_1') || 
            types.includes('premise') || 
            types.includes('point_of_interest') || 
            types.includes('establishment')) {
          location.sublocation = component.long_name;
          break;
        }
      }
    }
  }
  
  // Extract coordinates
  if (place.geometry && place.geometry.location) {
    location.geolocation = {
      latitude: place.geometry.location.lat(),
      longitude: place.geometry.location.lng()
    };
    
    // Generate static map URL
    location.static_map_url = generateMapboxStaticImageUrl(location.geolocation);
  }
  
  // Add Google Maps identifier
  if (place.place_id) {
    location.external_identifiers = [
      {
        provider: 'google_maps',
        id: place.place_id,
        url: `https://www.google.com/maps/place/?q=place_id:${place.place_id}`
      }
    ];
  }
  
  return location;
};
